import { useState } from "react";
import type { TrialStatusResponse } from "@/app/api/__generated__/models/trialStatusResponse";
import { Button } from "@/components/atoms/Button/Button";
import { Text } from "@/components/atoms/Text/Text";
import { Dialog } from "@/components/molecules/Dialog/Dialog";
import { formatTrialEnd } from "./helpers";

interface Props {
  trial: TrialStatusResponse;
  isCanceling: boolean;
  onCancel: () => void;
}

export function CancelTrialDialog({ trial, isCanceling, onCancel }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  function confirmCancellation() {
    setIsOpen(false);
    onCancel();
  }

  return (
    <Dialog
      title="Cancel your trial?"
      styling={{ maxWidth: "28rem", minWidth: "auto" }}
      controlled={{ isOpen, set: setIsOpen }}
    data-sentry-element="Dialog" data-sentry-component="CancelTrialDialog" data-sentry-source-file="CancelTrialDialog.tsx">
      <Dialog.Trigger data-sentry-element="Dialog.Trigger" data-sentry-source-file="CancelTrialDialog.tsx">
        <Button
          variant="outline"
          size="small"
          loading={isCanceling}
          disabled={isCanceling}
        data-sentry-element="Button" data-sentry-source-file="CancelTrialDialog.tsx">
          Cancel trial
        </Button>
      </Dialog.Trigger>
      <Dialog.Content data-sentry-element="Dialog.Content" data-sentry-source-file="CancelTrialDialog.tsx">
        <div
          data-slot="cancel-trial-dialog-body"
          className="flex flex-col gap-3"
        >
          <Text variant="body" tone="emphasis" data-sentry-element="Text" data-sentry-source-file="CancelTrialDialog.tsx">
            Your trial won&apos;t convert to a paid plan and your card
            won&apos;t be charged.
          </Text>
          <Text variant="body" tone="emphasis" data-sentry-element="Text" data-sentry-source-file="CancelTrialDialog.tsx">
            You keep full access until{" "}
            <strong
              data-slot="cancel-trial-dialog-end-date"
              className="font-semibold"
            >
              {formatTrialEnd(trial.ends_at)}
            </strong>
            , and you can resume your trial any time before then.
          </Text>
        </div>
        <Dialog.Footer data-sentry-element="Dialog.Footer" data-sentry-source-file="CancelTrialDialog.tsx">
          <Button variant="outline" onClick={() => setIsOpen(false)} data-sentry-element="Button" data-sentry-source-file="CancelTrialDialog.tsx">
            Keep trial
          </Button>
          <Button
            variant="primary"
            onClick={confirmCancellation}
            disabled={isCanceling}
          data-sentry-element="Button" data-sentry-source-file="CancelTrialDialog.tsx">
            Cancel trial
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}
